"use client";

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "../slice/hooks";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  normalizeName,
  generateProductUrl,
} from "../../lib/util";
import {
  getAccessPointsQueryKey,
  useFetchAccessPoint,
} from "../../lib/services/access-point-service";
import {
  getOrganisationQueryKey,
  useFetchOrganisation,
} from "../../lib/services/organisation-service";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";

export function useSwitchingOrgs({ token } = {}) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { navigateTo } = useHistoryManager();
  const [isSwitching, setSwitching] = useState(false);
  const [error, setError] = useState(null);
  const [organisationRequest, setOrganisationRequest] = useState(null);
  const [accessPointRequest, setAccessPointRequest] = useState(null);

  const buildStorePath = (handle, accessPointName) => {
    const cleanAP = normalizeName(accessPointName || "");
    return {
      storePath: `/store/${handle}${accessPointName ? `/${cleanAP}` : ""}`,
      basePath: `/store/${handle}`,
      cleanAP,
    };
  };

  const buildIncomingSlug = (handle, cleanAP) => {
    const url =
      cleanAP === "main-entrance" ? `${handle}` : `${handle}/${cleanAP}`;
    return String(url || "").replace(/^\/+|\/+$/g, "");
  };

  const buildRootedStorePath = (storePath, incoming) => {
    return (
      String(storePath)
        .replace(/(\/?store)(?:\/.*)?$/, "$1")
        .replace(/\/+$/, "") + (incoming ? `/${incoming}` : "")
    );
  };

  const buildNavigationPath = (data, newPath, incoming) => {
    if (data.productId) {
      const productPath = buildRootedStorePath(newPath.storePath, incoming);
      return generateProductUrl(productPath, data);
    }

    if (data.collectionId) {
      const collectionPath = buildRootedStorePath(newPath.storePath, incoming);
      return `${collectionPath}/checkout`;
    }

    return newPath.storePath + (incoming ? `/${incoming}` : "");
  };

  const pickMainAccessPoint = (data, accessPoints) => {
    if (data.accessPointId) {
      const match = accessPoints.find(
        (ap) => ap.accessPointId === data.accessPointId,
      );
      if (data.collectionId && !match) {
        return { accessPointId: data.accessPointId };
      }
      return match || null;
    }

    return accessPoints.length ? accessPoints[0] : null;
  };

  const finishSwitch = useCallback(
    ({ data, organisation, accessPoints, path }) => {
      const newAccessPoints = accessPoints || [];
      const mainAccessPoint = pickMainAccessPoint(data, newAccessPoints);

      const snapshot = {
        organisation,
        accessPoint: mainAccessPoint,
        accessPoints: newAccessPoints,
        accessPointId: data?.accessPointId,
        ...(data?.productId ? { product: data } : {}),
        ...(data?.collectionId ? { cart: data } : {}),
      };

      if (typeof window !== "undefined") {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }

      navigateTo(
        path,
        {
          urlData: snapshot,
          // debounceMs: 0,
          // override: true,
          onNavigate: (navigate) => {
            if (navigate) {
              dispatch(setOrganisation(organisation));
              if (newAccessPoints && newAccessPoints.length > 0) {
                if (data.accessPointId) {
                  dispatch(setAccessPointId(data.accessPointId));
                }
                dispatch(setAccessPoint(mainAccessPoint));
                dispatch(setAccessPoints(newAccessPoints));
              }
            }
            setSwitching(false);
          },
        },
        false,
      );
    },
    [dispatch, navigateTo],
  );

  const startAccessPointSwitch = useCallback(
    ({ data, organisation, path }) => {
      const cachedAccessPointsResponse = queryClient.getQueryData(
        getAccessPointsQueryKey(organisation.orgId, token),
      );
      const cachedAccessPoints =
        cachedAccessPointsResponse?.data?.accessPoints;

      if (cachedAccessPoints) {
        finishSwitch({
          data,
          organisation,
          accessPoints: cachedAccessPoints,
          path,
        });
        return;
      }

      setAccessPointRequest({
        orgId: organisation.orgId,
        token,
        organisation,
        data,
        path,
      });
    },
    [finishSwitch, queryClient, token],
  );

  const handleOrganisationResponse = useCallback(
    (orgRes, request) => {
      const organisation = orgRes?.data?.organisation;
      const switchData = request?.data;

      if (!organisation || !switchData) {
        setError(new Error("Organisation returned empty"));
        setSwitching(false);
        return;
      }

      startAccessPointSwitch({
        data: switchData,
        organisation,
        path: request.path,
      });
    },
    [startAccessPointSwitch],
  );

  useFetchOrganisation({
    orgId: organisationRequest?.orgId,
    token: organisationRequest?.token,
    enabled: Boolean(organisationRequest),
    onSuccess: (orgRes) => {
      const request = organisationRequest;

      setOrganisationRequest(null);

      handleOrganisationResponse(orgRes, request);
    },
    onError: (err) => {
      setOrganisationRequest(null);
      setError(err);
      setSwitching(false);
    },
  });

  useFetchAccessPoint({
    orgId: accessPointRequest?.orgId,
    token: accessPointRequest?.token,
    enabled: Boolean(accessPointRequest),
    onSuccess: (apRes) => {
      const request = accessPointRequest;
      const newAccessPoints = apRes?.data?.accessPoints || [];

      setAccessPointRequest(null);

      finishSwitch({
        data: request?.data,
        organisation: request?.organisation,
        accessPoints: newAccessPoints,
        path: request?.path,
      });
    },
    onError: (err) => {
      setAccessPointRequest(null);
      setError(err);
      setSwitching(false);
    },
  });

  const startSwitch = useCallback(
    (data = {}) => {
      if (!data || (!data.host && !data.collectionId && !data.productId)) {
        setError(new Error("Invalid data for startSwitch"));
        return;
      }

      setError(null);
      setSwitching(true);
      const { cleanAP, ...newPath } = buildStorePath(
        data.host.handle,
        data.accessPointName,
      );
      const incoming = buildIncomingSlug(data.host.handle, cleanAP);
      const path = buildNavigationPath(data, newPath, incoming);
      const request = {
        orgId: data.host?.profileId,
        token,
        data,
        path,
      };

      const cachedOrganisationResponse = queryClient.getQueryData(
        getOrganisationQueryKey(request.orgId, token),
      );

      if (cachedOrganisationResponse) {
        handleOrganisationResponse(cachedOrganisationResponse, request);
        return;
      }

      setOrganisationRequest(request);
    },
    [handleOrganisationResponse, queryClient, token],
  );

  return { startSwitch, isSwitching, error };
}
